import AddIcon from '@mui/icons-material/Add'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import HistoryIcon from '@mui/icons-material/History'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { CardListTable } from '~/features/adv_account/CardList'
import { useAuth } from '~/contexts/AuthContext'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getCardEditUrl, getCardList } from '~/api/PaymentRepository'
import { GMOPG_API_UNAVAILABLE_MESSAGE, PaymentMethodKind, type CardList } from '~/lib/types/AdvAccount'
import NextLink from 'next/link'
import { SectionTitle } from '~/components/common/SectionTitle'
import { pagesPath } from '~/lib/$path'
import { PageContainer } from '~/components/common/PageContainer'
import PayjpCardRegister from '~/features/adv_account/PayjpCardRegister'
import { motion } from 'framer-motion'
import { useAdvPaymentMethod } from '~/lib/hooks/useAdvPaymentMethod'
import { snackActions } from '~/lib/snackbarHelper'
import { logger } from '~/lib/logger'

const MAX_CARD_NUMS = 5

const CreditRegistration = () => {
  const { currentAdv, currentUserAdv, refetchCurrentAdv } = useAuth()
  const { registeredPaymentMethod } = useAdvPaymentMethod()
  const isGmopgApiUnavailable = currentAdv.isGmopgApiAvailable === false
  const gmopgApiUnavailableMessage = currentAdv.errorMessage ?? GMOPG_API_UNAVAILABLE_MESSAGE

  const [loading, setLoading] = useState(true)
  const [cardList, setCardList] = useState<CardList>()

  const hasReachedMaxCards = useMemo(() => {
    if (!cardList) {
      return false
    }

    return cardList.cards.data.length >= MAX_CARD_NUMS
  }, [cardList])

  const disabledAddCardButton = isGmopgApiUnavailable || hasReachedMaxCards

  const fetchData = useCallback(async () => {
    if (isGmopgApiUnavailable) {
      setCardList(undefined)
      setLoading(false)
      return
    }

    getCardList()
      .then((res) => {
        setCardList(res)
      })
      .catch((e) => {
        logger.error(new Error("Failed to fetch user's credit card information", { cause: e }))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isGmopgApiUnavailable])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refresh = () => {
    // カード作成/登録が完了したら一覧を再読込みする
    fetchData()
    // currentAdvを更新
    refetchCurrentAdv()
  }

  const payjpAction = currentAdv.isRetrievePayjpCustomer ? 'add' : 'create'

  const [payjpRegisterDialogOpen, setPayjpRegisterDialogOpen] = useState(false)
  const [agreedPolicy, setAgreedPolicy] = useState(false)

  const handleAddCard = () => {
    if (registeredPaymentMethod === PaymentMethodKind.CreditCard) {
      setPayjpRegisterDialogOpen(true)
      return
    }

    getCardEditUrl()
      .then(({ url }) => {
        window.location.href = url
      })
      .catch((e) => {
        snackActions.error('カードの追加に失敗しました')
        logger.error(new Error('Failed to get card edit url', { cause: e }))
      })
  }

  return (
    <PageContainer>
      <Stack gap={6}>
        {isGmopgApiUnavailable && <Alert severity="error">{gmopgApiUnavailableMessage}</Alert>}

        <Stack gap={2}>
          <SectionTitle icon={<AddIcon />}>新規クレジットカードの追加</SectionTitle>
          <Stack direction="row" gap={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddCard} disabled={disabledAddCardButton}>
              クレジットカードを追加する
            </Button>
          </Stack>
          <Stack gap={2}>
            {hasReachedMaxCards && (
              <Typography variant="body2" color="error.main">
                クレジットカード枚数の上限に達しました。
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              各種請求の決済に使用するクレジットカードは、こちらから追加してください。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              次回の新規キャンペーン及びチケット付与から新しいカードで決済が始まります。
            </Typography>
          </Stack>
        </Stack>

        <Stack gap={2}>
          <SectionTitle icon={<CreditCardIcon />}>登録済みクレジットカード情報</SectionTitle>
          {loading ? (
            <Skeleton height={300} />
          ) : isGmopgApiUnavailable ? (
            <Typography variant="body2" color="text.secondary">
              クレジットカード情報を取得できません。
            </Typography>
          ) : cardList ? (
            <>
              <Box>
                <NextLink href={pagesPath.creditcards.payment_histories.$url()} passHref legacyBehavior>
                  <Button disabled={!cardList} variant="outlined" component="a" startIcon={<HistoryIcon />}>
                    クレジットカード支払い履歴を確認する
                  </Button>
                </NextLink>
              </Box>
              <CardListTable cardList={cardList} fetchData={fetchData} paymentMethod={registeredPaymentMethod} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              クレジットカード情報が登録されていません。
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* PAY.JPのカード登録ダイアログ */}
      <Dialog
        open={payjpRegisterDialogOpen}
        onClose={() => setPayjpRegisterDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="card-register-dialog-title"
        PaperProps={{ component: motion.div, layout: true, transition: { duration: 0.1, ease: 'easeOut' } }}
      >
        <DialogTitle variant="headingM" sx={{ fontWeight: '500' }} id="card-register-dialog-title">
          クレジットカードを登録
        </DialogTitle>
        <DialogContent>
          {agreedPolicy ? (
            // DialogContentのoverflowプロパティによりabsolute配置しているTextFieldのラベルが見切れるため余白を追加する
            <Box pt={1}>
              <PayjpCardRegister
                action={payjpAction}
                userAdv={currentUserAdv}
                onRegistered={() => {
                  setPayjpRegisterDialogOpen(false)
                  refresh()
                }}
              />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Typography variant="body1">利用上の注意</Typography>
              <Typography variant="body2">
                クレジットカード番号などの情報の盗用による不正利用を防ぎ、安全にクレジットカードをご利用いただくため、国際ブランドの推奨する本人認証サービス「EMV
                ３D
                セキュア」を用いています。EMV3Dセキュアを利用するためには、カード発行会社に、お客様に関する情報などを送信する必要がございます。
                以下の個人情報の第三者提供をご確認いただき、同意の上カード情報をご入力ください。
              </Typography>

              <Typography variant="body1">個人情報の第三者提供</Typography>
              <Typography variant="body2">
                お客様から収集した以下の個人情報等は、カード発行会社が行う不正利用検知‧防止のために、お客様が利用されているカードの発行会社へ提供させていただきます。
                氏名、電話番号、emailアドレス、インターネット利用環境に関する情報 等
              </Typography>

              <Typography variant="body2">
                お客様が利用されているカードの発行会社が外国にある場合、これらの情報は当該発行会社が所属する国に移転される場合があります。
                お客様が未成年の場合、親権者または後見人の承諾を得た上で、本サービスを利用するものとします。
              </Typography>
            </Stack>
          )}
        </DialogContent>
        {!agreedPolicy && (
          <DialogActions>
            <Stack direction="row" spacing={2} alignItems="end">
              <Button variant="contained" onClick={() => setAgreedPolicy(true)}>
                同意する
              </Button>
              <Button variant="outlined" onClick={() => setPayjpRegisterDialogOpen(false)}>
                同意しない
              </Button>
            </Stack>
          </DialogActions>
        )}
      </Dialog>
    </PageContainer>
  )
}

export default CreditRegistration
